import { Button, Checkbox, Popover, PopoverContent, PopoverTrigger } from '@planuze/ui';
import { ChevronDown } from 'lucide-react';
import { useFiltersContent } from '../../content/sections/filters.js';

/* Filtro de faceta multi-seleção (ADR-0704): trigger com rótulo + contador de selecionados e um
   popover com checkboxes. Para dimensões com muitas opções (tier, papel, categoria, severidade)
   onde segmented control não cabe. Filtragem é client-side no call site (opções derivadas dos
   dados carregados). */
interface FacetOption {
  readonly value: string;
  readonly label: string;
}

export function FacetMultiSelect({
  label,
  values,
  onChange,
  options,
}: {
  readonly label: string;
  readonly values: readonly string[];
  readonly onChange: (values: string[]) => void;
  readonly options: readonly FacetOption[];
}) {
  const copy = useFiltersContent().facetMultiSelect;
  const toggle = (v: string): void => {
    onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  };
  const count = values.length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-1.5">
          {label}
          {count > 0 && (
            <span className="rounded-full bg-accent/15 px-1.5 text-xs tabular-nums text-foreground">
              {count}
            </span>
          )}
          <ChevronDown className="size-4 text-muted-foreground" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1">
        {options.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-muted-foreground">{copy.noOptions}</p>
        ) : (
          <ul className="max-h-64 overflow-auto">
            {options.map((opt) => {
              const id = `facet-opt-${opt.value}`;
              return (
                <li key={opt.value}>
                  <label
                    htmlFor={id}
                    className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent/15"
                  >
                    <Checkbox
                      id={id}
                      checked={values.includes(opt.value)}
                      onCheckedChange={() => toggle(opt.value)}
                    />
                    <span className="truncate">{opt.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        {count > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="mt-1 w-full rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent/15 hover:text-foreground"
          >
            {copy.clearSelection}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
